import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import type {
  WebsiteContactInfoDto,
  WebsitePageDto,
} from '../../../../core/models/website-content/website-content.models';
import { SeoMetaService } from '../../../../core/services/seo-meta.service';
import { StructuredDataService } from '../../../../core/services/structured-data.service';
import { WebsiteContentService } from '../../../../core/services/website-content.service';
import { TokenStorageService } from '../../../../core/storage/token-storage.service';
import { PublicShellLayout } from '../../../../shared/components/public-shell-layout/public-shell-layout';

@Component({
  selector: 'app-public-content-page',
  standalone: true,
  imports: [PublicShellLayout, RouterLink, TranslatePipe],
  templateUrl: './public-content-page.html',
  styleUrl: './public-content-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicContentPage implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly service = inject(WebsiteContentService);
  private readonly seoMeta = inject(SeoMetaService);
  private readonly structuredData = inject(StructuredDataService);
  private readonly tokens = inject(TokenStorageService);

  protected readonly isSignedIn = signal(this.tokens.hasValidTokens());
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly page = signal<WebsitePageDto | null>(null);
  protected readonly contact = signal<WebsiteContactInfoDto | null>(null);

  protected get isContactPage(): boolean {
    return this.slug() === 'contact-us';
  }

  protected phoneDisplayNumber(value: string | null | undefined): string {
    const normalized = String(value ?? '').trim().replace(/\s+/g, ' ');
    if (!normalized || normalized.startsWith('+')) {
      return normalized;
    }

    const suffixPrefixMatch = normalized.match(/^(.+?)\s+(\+\d{1,4})$/);
    return suffixPrefixMatch
      ? `${suffixPrefixMatch[2]} ${suffixPrefixMatch[1]}`.trim()
      : normalized;
  }

  protected phoneHref(value: string | null | undefined): string {
    const normalized = this.phoneDisplayNumber(value);
    const tel = normalized.replace(/[^\d+]/g, '');
    return tel ? `tel:${tel}` : '';
  }

  private slug(): string {
    return String(this.route.snapshot.data['slug'] ?? '').trim();
  }

  ngOnInit(): void {
    const slug = this.slug();
    if (!slug) {
      this.error.set('COMMON.LOAD_FAILED');
      this.loading.set(false);
      return;
    }

    const page$ = this.service.getPage(slug);
    const contact$ = slug === 'contact-us'
      ? this.service.getContactInfo().pipe(catchError(() => of(null)))
      : of(null);

    forkJoin({ page: page$, contact: contact$ })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: ({ page, contact }) => {
          this.page.set(page);
          this.contact.set(contact);
          if (contact) {
            this.structuredData.setContactPage(contact);
          }
          this.seoMeta.setPageMeta({
            title: `${page.title} | WorkNest HR`,
            description: page.metaDescription || page.content.slice(0, 150),
            path: `/${this.slug()}`,
          });
        },
        error: () => {
          this.error.set('COMMON.LOAD_FAILED');
        },
      });
  }
}
